/**
 * API client for Cloud Functions
 */

import { auth } from './firebase';
import { Product, Order, User, PaginatedResponse, ContactSubmission } from '../types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:5001/foxie-systems-dev/us-central1/api';

/**
 * Get authentication token
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  return user.getIdToken();
}

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Request failed',
    }));

    // Include status code and more details in error message
    const errorMessage = error.error || 'Request failed';
    const statusText = response.statusText || 'Unknown Error';
    const fullError = `${errorMessage} (${response.status} ${statusText})`;

    // Log full error details for debugging
    console.error('API Error:', {
      status: response.status,
      statusText: response.statusText,
      endpoint,
      error: error,
    });

    throw new Error(fullError);
  }

  return response.json();
}

/**
 * Products API
 */
export const productsApi = {
  /**
   * List all published products
   */
  async list(params?: {
    category?: string;
    tier?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ products: Product[] }> {
    const query = new URLSearchParams(params as any).toString();
    return apiRequest(`/products?${query}`);
  },

  /**
   * Get product by ID
   */
  async get(id: string): Promise<Product> {
    return apiRequest(`/products/${id}`);
  },

  /**
   * Search products
   */
  async search(query: string): Promise<{ products: Product[] }> {
    return apiRequest(`/products/search/${encodeURIComponent(query)}`);
  },

  /**
   * Create product (admin only)
   */
  async create(product: Partial<Product>): Promise<Product> {
    return apiRequest('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  },

  /**
   * Update product (admin only)
   */
  async update(id: string, updates: Partial<Product>): Promise<void> {
    return apiRequest(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete product (admin only)
   */
  async delete(id: string): Promise<void> {
    return apiRequest(`/products/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Publish product (admin only)
   */
  async publish(id: string): Promise<void> {
    return apiRequest(`/products/${id}/publish`, {
      method: 'POST',
    });
  },

  /**
   * Unpublish product (admin only)
   */
  async unpublish(id: string): Promise<void> {
    return apiRequest(`/products/${id}/unpublish`, {
      method: 'POST',
    });
  },
};

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Get user's orders
   */
  async list(): Promise<{ orders: Order[] }> {
    return apiRequest('/orders');
  },

  /**
   * Get order by ID
   */
  async get(id: string): Promise<Order> {
    return apiRequest(`/orders/${id}`);
  },

  /**
   * Request refund
   */
  async refund(id: string): Promise<void> {
    return apiRequest(`/orders/${id}/refund`, {
      method: 'POST',
    });
  },
};

/**
 * Auth API
 */
export const authApi = {
  /**
   * Register new user
   */
  async register(data: {
    email: string;
    password: string;
    displayName?: string;
  }): Promise<{ uid: string }> {
    return apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get current user info
   */
  async me(): Promise<User> {
    return apiRequest('/auth/me');
  },
};

/**
 * Admin API
 */
export const adminApi = {
  /**
   * List all users with filtering and pagination
   */
  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<User>> {
    const query = new URLSearchParams(params as any).toString();
    return apiRequest(`/admin/users?${query}`);
  },

  /**
   * List all orders with filtering and pagination
   */
  async listOrders(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    totalMin?: number;
    totalMax?: number;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Order>> {
    const query = new URLSearchParams(params as any).toString();
    return apiRequest(`/admin/orders?${query}`);
  },

  /**
   * List all products (including unpublished) with filtering and pagination
   */
  async listProducts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    published?: boolean;
    qualityTier?: string;
    priceMin?: number;
    priceMax?: number;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Product>> {
    const query = new URLSearchParams(params as any).toString();
    return apiRequest(`/admin/products?${query}`);
  },

  /**
   * Get platform statistics
   */
  async getStats(): Promise<{
    // Basic counts
    totalUsers: number;
    totalProducts: number;
    totalOrders: number;
    // Product breakdown
    publishedProducts: number;
    draftProducts: number;
    // Order breakdown
    completedOrders: number;
    pendingOrders: number;
    refundedOrders: number;
    // Revenue
    totalRevenue: number;
    // Users
    adminUsers: number;
    // Contacts
    newContacts: number;
    // Rates
    conversionRate: number;
    refundRate: number;
  }> {
    return apiRequest('/admin/stats');
  },

  /**
   * Get detailed analytics data
   */
  async getAnalytics(period: 'today' | '7d' | '30d' | '90d' | 'all' = '30d'): Promise<any> {
    return apiRequest(`/admin/analytics?period=${period}`);
  },

  /**
   * Initialize default roles
   */
  async initializeRoles(): Promise<void> {
    return apiRequest('/admin/initialize-roles', {
      method: 'POST',
    });
  },

  /**
   * Get all roles
   */
  async getRoles(): Promise<{ roles: any[] }> {
    return apiRequest('/admin/roles');
  },

  /**
   * Create a new role
   */
  async createRole(roleData: { name: string; description: string; permissions: string[] }): Promise<any> {
    return apiRequest('/admin/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  },

  /**
   * Update a role
   */
  async updateRole(roleId: string, updates: { description?: string; permissions?: string[] }): Promise<any> {
    return apiRequest(`/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete a role
   */
  async deleteRole(roleId: string): Promise<any> {
    return apiRequest(`/admin/roles/${roleId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Assign roles to a user
   */
  async assignUserRoles(userId: string, roleIds: string[]): Promise<any> {
    return apiRequest(`/admin/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleIds }),
    });
  },

  /**
   * Bulk publish/unpublish products
   */
  async bulkPublishProducts(
    productIds: string[],
    published: boolean
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    return apiRequest('/admin/products/bulk-publish', {
      method: 'POST',
      body: JSON.stringify({ productIds, published }),
    });
  },

  /**
   * Bulk delete products
   */
  async bulkDeleteProducts(
    productIds: string[]
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    return apiRequest('/admin/products/bulk-delete', {
      method: 'DELETE',
      body: JSON.stringify({ productIds }),
    });
  },

  /**
   * Bulk update products (category or quality tier)
   */
  async bulkUpdateProducts(
    productIds: string[],
    updates: { category?: string; qualityTier?: string }
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    return apiRequest('/admin/products/bulk-update', {
      method: 'PATCH',
      body: JSON.stringify({ productIds, updates }),
    });
  },

  /**
   * Bulk refund orders
   */
  async bulkRefundOrders(
    orderIds: string[]
  ): Promise<{ success: number; failed: number; skipped: number; errors: string[] }> {
    return apiRequest('/admin/orders/bulk-refund', {
      method: 'POST',
      body: JSON.stringify({ orderIds }),
    });
  },

  /**
   * Export orders to CSV
   */
  async exportOrdersCSV(
    orderIds?: string[],
    filters?: any
  ): Promise<Blob> {
    const token = await getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/admin/orders/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ orderIds, filters }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Export failed',
      }));
      throw new Error(error.error || 'Export failed');
    }

    return response.blob();
  },

  /**
   * Manually trigger static content regeneration
   */
  async regenerateStaticContent(): Promise<{ success: boolean; message: string }> {
    return apiRequest('/admin/regenerate-static-content', {
      method: 'POST',
    });
  },

  /**
   * List contact submissions with filtering and pagination
   */
  async listContacts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    subject?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<ContactSubmission>> {
    const query = new URLSearchParams(params as any).toString();
    return apiRequest(`/contact?${query}`);
  },

  /**
   * Get a single contact submission
   */
  async getContact(id: string): Promise<ContactSubmission> {
    return apiRequest(`/contact/${id}`);
  },

  /**
   * Update contact submission status and notes
   */
  async updateContact(
    id: string,
    updates: { status?: string; adminNotes?: string }
  ): Promise<{ success: boolean }> {
    return apiRequest(`/contact/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete a contact submission
   */
  async deleteContact(id: string): Promise<{ success: boolean }> {
    return apiRequest(`/contact/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Downloads API
 */
export const downloadsApi = {
  /**
   * Download a product file (streams through backend for tracking)
   */
  async downloadProduct(orderId: string, productId: string): Promise<void> {
    const token = await getAuthToken();
    if (!token) throw new Error('Authentication required');

    // Use the same base URL as API but with /getDocument path
    const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') ||
      window.location.origin;

    const response = await fetch(`${baseUrl}/getDocument?orderId=${orderId}&productId=${productId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Download failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Get blob from response
    const blob = await response.blob();

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

/**
 * Contact API
 */
export const contactApi = {
  /**
   * Submit a contact form
   */
  async submit(data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<{ success: boolean; id: string; message: string }> {
    return apiRequest('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Unified API object with Axios-like interface for easier usage
 */
export const api = {
  get: async <T = any>(url: string, config?: { headers?: Record<string, string> }): Promise<{ data: T }> => {
    const result = await apiRequest<T>(url, {
      method: 'GET',
      headers: config?.headers,
    });
    return { data: result };
  },

  post: async <T = any>(
    url: string,
    data?: any,
    config?: { headers?: Record<string, string> }
  ): Promise<{ data: T }> => {
    const result = await apiRequest<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: config?.headers,
    });
    return { data: result };
  },

  put: async <T = any>(
    url: string,
    data?: any,
    config?: { headers?: Record<string, string> }
  ): Promise<{ data: T }> => {
    const result = await apiRequest<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: config?.headers,
    });
    return { data: result };
  },

  delete: async <T = any>(url: string, config?: { headers?: Record<string, string> }): Promise<{ data: T }> => {
    const result = await apiRequest<T>(url, {
      method: 'DELETE',
      headers: config?.headers,
    });
    return { data: result };
  },

  // Named API endpoints for convenience
  products: productsApi,
  orders: ordersApi,
  auth: authApi,
  admin: adminApi,
  downloads: downloadsApi,
  contact: contactApi,
};
